/**
 * Menu Engineering Matrix
 * 2x2 Matrix visualization: Stars, Plowhorses, Puzzles, Dogs
 */

import { MenuItemAnalysis, MENU_CATEGORY_COLORS, MenuCategory } from '../../types/analytics';

interface MenuEngineeringMatrixProps {
  items: MenuItemAnalysis[];
  avgPopularity: number;
  avgMargin: number;
}

export function MenuEngineeringMatrix({ items, avgPopularity, avgMargin }: MenuEngineeringMatrixProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-slate-400">
        Nessun dato disponibile
      </div>
    );
  }

  // Group items by category
  const stars = items.filter(i => i.menuCategory === MenuCategory.STAR);
  const plowhorses = items.filter(i => i.menuCategory === MenuCategory.PLOWHORSE);
  const puzzles = items.filter(i => i.menuCategory === MenuCategory.PUZZLE);
  const dogs = items.filter(i => i.menuCategory === MenuCategory.DOG);

  const CategoryBox = ({
    title,
    emoji,
    items,
    color,
    description
  }: {
    title: string;
    emoji: string;
    items: MenuItemAnalysis[];
    color: string;
    description: string;
  }) => (
    <div
      className="border-2 rounded-lg p-4 h-full bg-white dark:bg-slate-900/30 dark:backdrop-blur-sm transition-all duration-300"
      style={{ borderColor: color }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{emoji}</span>
        <div>
          <h3 className="font-bold text-lg dark:drop-shadow-[0_1px_5px_rgba(255,255,255,0.1)]" style={{ color }}>{title}</h3>
          <p className="text-xs text-gray-600 dark:text-slate-400">{description}</p>
        </div>
        <span className="ml-auto bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-2 py-1 rounded-full text-sm font-bold">
          {items.length}
        </span>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {items.slice(0, 10).map((item) => (
          <div
            key={item.productId}
            className="p-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-900 dark:text-white">{item.productName}</span>
              <span className="text-xs text-gray-500 dark:text-slate-400">#{item.popularityRank}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-1 text-xs text-gray-600 dark:text-slate-300">
              <div>
                <span className="font-medium">Pop:</span> {item.popularityIndex.toFixed(0)}%
              </div>
              <div>
                <span className="font-medium">Margin:</span> €{item.contributionMargin.toFixed(2)}
              </div>
              <div>
                <span className="font-medium">Sold:</span> {item.quantitySold}
              </div>
            </div>
            {item.suggestedPrice && (
              <div className="mt-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                💡 Prezzo suggerito: €{item.suggestedPrice.toFixed(2)}
              </div>
            )}
          </div>
        ))}
        {items.length > 10 && (
          <div className="text-center text-xs text-gray-500 dark:text-slate-400 py-2">
            +{items.length - 10} altri piatti
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 2x2 Matrix */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Row: Puzzles | Stars */}
        <CategoryBox
          title="Puzzle"
          emoji="🧩"
          items={puzzles}
          color={MENU_CATEGORY_COLORS[MenuCategory.PUZZLE]}
          description="Bassa popolarità, alto profitto"
        />
        <CategoryBox
          title="Star"
          emoji="⭐"
          items={stars}
          color={MENU_CATEGORY_COLORS[MenuCategory.STAR]}
          description="Alta popolarità, alto profitto"
        />

        {/* Bottom Row: Dogs | Plowhorses */}
        <CategoryBox
          title="Dog"
          emoji="🐕"
          items={dogs}
          color={MENU_CATEGORY_COLORS[MenuCategory.DOG]}
          description="Bassa popolarità, basso profitto"
        />
        <CategoryBox
          title="Plowhorse"
          emoji="🐴"
          items={plowhorses}
          color={MENU_CATEGORY_COLORS[MenuCategory.PLOWHORSE]}
          description="Alta popolarità, basso profitto"
        />
      </div>

      {/* Axis Labels */}
      <div className="relative mt-8">
        <div className="flex items-center justify-center">
          <div className="relative w-full max-w-2xl">
            {/* Vertical axis (Profitability) */}
            <div className="absolute -left-20 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-gray-700 dark:text-slate-300">
              PROFITTO →
            </div>

            {/* Horizontal axis (Popularity) */}
            <div className="text-center text-sm font-medium text-gray-700 dark:text-slate-300 mt-4">
              ← POPOLARITÀ →
            </div>

            {/* Center lines */}
            <div className="absolute top-1/2 left-0 right-0 border-t-2 border-dashed border-gray-300 dark:border-slate-400 -translate-y-1/2"></div>
            <div className="absolute left-1/2 top-0 bottom-0 border-l-2 border-dashed border-gray-300 dark:border-slate-400 -translate-x-1/2"></div>

            {/* Average values */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-slate-300">
              Avg Popularity: {avgPopularity.toFixed(1)}%
            </div>
            <div className="absolute -right-24 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-slate-300 whitespace-nowrap">
              Avg Margin: €{avgMargin.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 dark:ring-1 dark:ring-green-500/20 rounded-lg p-4 text-center transition-all duration-300">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stars.length}</div>
          <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">Stars ⭐</div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Mantieni e promuovi</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 dark:ring-1 dark:ring-amber-500/20 rounded-lg p-4 text-center transition-all duration-300">
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{plowhorses.length}</div>
          <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">Plowhorses 🐴</div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Aumenta prezzo</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700/50 dark:ring-1 dark:ring-purple-500/20 rounded-lg p-4 text-center transition-all duration-300">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{puzzles.length}</div>
          <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">Puzzles 🧩</div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Migliora marketing</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 dark:ring-1 dark:ring-red-500/20 rounded-lg p-4 text-center transition-all duration-300">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">{dogs.length}</div>
          <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">Dogs 🐕</div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Valuta rimozione</div>
        </div>
      </div>
    </div>
  );
}
